import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import QRCode from "qrcode";
import {
  UserPlus, X, WhatsappLogo, Envelope, Copy, XLogo, ShareNetwork, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// InviteFriends — small, reliable share sheet. QR (client-side, no server round-trip),
// WhatsApp deep link, mailto, X, native share on capable devices, copy-link fallback.

const INVITE_URL = "https://naturepulse.net?ref=invite";
const INVITE_MESSAGE = "I've been using NaturePulse — it's a beautiful, action-oriented app for local air quality, wildlife and simple climate actions. Take a look and join me: " + INVITE_URL;

export default function InviteFriends({ variant = "chip", label = "Invite someone who cares about the planet" }) {
  const [open, setOpen] = useState(false);
  const [qr, setQr] = useState("");
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  useEffect(() => {
    if (!open) return;
    QRCode.toDataURL(INVITE_URL, {
      margin: 1,
      width: 512,
      color: { dark: "#151312", light: "#F5EBDC" },
      errorCorrectionLevel: "M",
    }).then(setQr).catch(() => setQr(""));
  }, [open]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(INVITE_URL);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy link."); }
  };

  const openNativeShare = async () => {
    try {
      await navigator.share({
        title: "NaturePulse",
        text: "Local air quality, wildlife & climate — beautifully.",
        url: INVITE_URL,
      });
    } catch { /* user cancelled */ }
  };

  const downloadQr = () => {
    if (!qr) return;
    const a = document.createElement("a");
    a.href = qr;
    a.download = "naturepulse-invite.png";
    a.click();
  };

  const Trigger = (
    <button
      data-testid="invite-trigger"
      onClick={() => setOpen(true)}
      className={
        variant === "chip"
          ? "inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase border border-[color:var(--np-brand,#B85F2A)] text-[color:var(--np-brand,#B85F2A)] hover:bg-[color:var(--np-brand,#B85F2A)] hover:text-white transition-colors"
          : variant === "full"
            ? "w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-mono-tech text-[11px] tracking-[0.3em] uppercase bg-[#151312] text-[#F5EBDC] hover:opacity-90"
            : "inline-flex items-center gap-2"
      }
    >
      <UserPlus size={14} weight="fill" />
      {label}
    </button>
  );

  return (
    <>
      {Trigger}
      {createPortal(
        <AnimatePresence>
          {open && (
            <>
              <motion.div
                className="fixed inset-0 z-[80] bg-black/60"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={() => setOpen(false)}
              />
              <motion.div
                data-testid="invite-sheet"
                className="fixed z-[81] inset-x-0 bottom-0 md:inset-0 md:m-auto md:h-fit md:w-[480px] max-h-[92dvh] overflow-y-auto rounded-t-2xl md:rounded-2xl bg-[#F5EBDC] text-[#151312] shadow-[0_-24px_60px_rgba(0,0,0,.4)] border border-black/10"
                initial={{ y: 400, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 400, opacity: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 28 }}
                style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
              >
                {/* Header */}
                <div className="px-5 pt-5 pb-2 flex items-center justify-between">
                  <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">/ Invite to NaturePulse</div>
                  <button aria-label="Close" data-testid="invite-close" onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-black/5">
                    <X size={16} weight="bold" />
                  </button>
                </div>

                <div className="px-5 pb-5">
                  <h2 className="font-serif-display text-3xl md:text-4xl leading-tight tracking-tight mb-2">
                    Bring someone who cares about the planet.
                  </h2>
                  <p className="text-sm text-[#3A3830] mb-4">
                    NaturePulse works best when neighbours, families, and classrooms are on it together. Share the link or the QR — everything works offline once installed.
                  </p>

                  {/* QR + link */}
                  <div className="rounded-xl border border-black/10 bg-white/70 backdrop-blur p-4 flex flex-col md:flex-row items-center gap-4">
                    <div className="shrink-0 w-40 h-40 rounded-lg overflow-hidden border border-black/10 bg-[#F5EBDC] grid place-items-center">
                      {qr ? (
                        <img src={qr} alt="NaturePulse invite QR" width="160" height="160" data-testid="invite-qr" />
                      ) : (
                        <div className="text-xs font-mono-tech tracking-widest uppercase text-[color:var(--np-muted)]">…</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 w-full">
                      <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] mb-1">Your invite link</div>
                      <div className="flex items-center gap-2">
                        <input
                          readOnly
                          value={INVITE_URL}
                          data-testid="invite-link"
                          onClick={(e) => e.currentTarget.select()}
                          className="flex-1 bg-transparent border border-black/15 rounded-full px-3 py-2 font-mono-tech text-[11px] tracking-[0.05em]"
                        />
                        <button
                          onClick={copyLink}
                          data-testid="invite-copy"
                          className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase border border-black/25 hover:bg-[#151312] hover:text-[#F5EBDC]"
                        >
                          {copied ? <CheckCircle size={12} weight="fill" /> : <Copy size={12} weight="bold" />}
                          {copied ? "Copied" : "Copy"}
                        </button>
                      </div>
                      <button
                        onClick={downloadQr}
                        data-testid="invite-qr-download"
                        className="mt-2 inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline"
                      >
                        Download QR
                      </button>
                    </div>
                  </div>

                  {/* Channel buttons */}
                  <div className="mt-4 grid grid-cols-2 gap-2.5">
                    <a
                      data-testid="invite-whatsapp"
                      href={`https://wa.me/?text=${encodeURIComponent(INVITE_MESSAGE)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 rounded-full py-3 text-white font-mono-tech text-[10px] tracking-[0.3em] uppercase bg-[#25D366] hover:opacity-90"
                    >
                      <WhatsappLogo size={14} weight="fill" /> WhatsApp
                    </a>
                    <a
                      data-testid="invite-email"
                      href={`mailto:?subject=${encodeURIComponent("A little pulse of the planet")}&body=${encodeURIComponent(INVITE_MESSAGE)}`}
                      className="inline-flex items-center justify-center gap-2 rounded-full py-3 font-mono-tech text-[10px] tracking-[0.3em] uppercase border border-black/25 hover:bg-[#151312] hover:text-[#F5EBDC]"
                    >
                      <Envelope size={14} weight="fill" /> Email
                    </a>
                    <a
                      data-testid="invite-x"
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(INVITE_MESSAGE)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 rounded-full py-3 font-mono-tech text-[10px] tracking-[0.3em] uppercase border border-black/25 hover:bg-[#151312] hover:text-[#F5EBDC]"
                    >
                      <XLogo size={14} weight="fill" /> X / Twitter
                    </a>
                    {canShare ? (
                      <button
                        data-testid="invite-native"
                        onClick={openNativeShare}
                        className="inline-flex items-center justify-center gap-2 rounded-full py-3 font-mono-tech text-[10px] tracking-[0.3em] uppercase bg-gradient-to-br from-[#B85F2A] to-[#E4A15C] text-white hover:brightness-110"
                      >
                        <ShareNetwork size={14} weight="fill" /> Share…
                      </button>
                    ) : (
                      <button
                        onClick={copyLink}
                        className="inline-flex items-center justify-center gap-2 rounded-full py-3 font-mono-tech text-[10px] tracking-[0.3em] uppercase border border-black/25 hover:bg-[#151312] hover:text-[#F5EBDC]"
                      >
                        <Copy size={14} weight="bold" /> Copy link
                      </button>
                    )}
                  </div>

                  <p className="mt-4 text-[11px] text-[color:var(--np-muted,#3A3830)]">
                    No account required for your friend — they can browse anonymously and level up when ready.
                  </p>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
